import { Auth } from 'aws-amplify';
import { type Dispatch } from 'react';

import { executeQuery2 } from '../../Admin/Datasources/AdminDatasource';
import { type QueryFactory2 } from '../../Admin/Datasources/QueryFactory';
import { ADMIN_LINK_PREFIX } from '../../Admin/Utils/LinkPrefix';
import { type CreateContactInput } from '../../API';
import { getContactIsCrewable } from '../../Datasource/Contacts/ContactsDatasource';
import { getDbSyncer } from '../../Datasource/LocalDB/DbSyncer';
import { contactItemFields } from '../../Datasource/LocalDB/Transformers/contacts';
import { type DbCreated } from '../../Datasource/LocalDB/types';
import { type User } from '../../Datasource/Users/User';
import { getUser, getUserFromContact } from '../../Datasource/Users/UserDatasource';
import {
    COGNITO_USER_AUTH_ERROR,
    COGNITO_USER_SIGN_IN,
    COGNITO_USER_SIGN_PROCESS_START,
    type CognitoUserActionTypes,
} from '../../reducers/cognitoUserReducer';
import {
    TLB_USER_AUTH_ERROR,
    TLB_USER_SIGN_IN,
    TLB_USER_SIGN_OUT,
    TLB_USER_SIGN_PROCESS_START,
    type TlbUserActionTypes,
} from '../../reducers/tlbUserReducer';
import { AppEnvironment, getPreferredEnvironment, setPreferredEnvironment } from '../BackendSwitcher';
import { isImpersonating } from '../Impersonation/impersonation';
import { isGenuineAuthInvalidationError } from './isGenuineAuthInvalidationError';

let waitingForOnline = false;

export const reauthenticate = async (
    dispatch: Dispatch<CognitoUserActionTypes | TlbUserActionTypes>
): Promise<void> => {
    if (!navigator.onLine) {
        // the auth flow always fails with a "user not authenticated" message,
        // logging off the user, even if the cause is the client is offline
        // - so run it once the device is back online, or the user stays marked stale
        if (!waitingForOnline) {
            waitingForOnline = true;
            globalThis.addEventListener(
                'online',
                () => {
                    waitingForOnline = false;
                    void reauthenticate(dispatch);
                },
                { once: true }
            );
        }
        return;
    }
    dispatch({ type: TLB_USER_SIGN_PROCESS_START });
    dispatch({ type: COGNITO_USER_SIGN_PROCESS_START });
    try {
        // Auth.currentUserPoolUser(), unlike Auth.currentAuthenticatedUser(), does not collapse
        // every failure into the same generic "The user is not authenticated" string - it rejects
        // with the literal 'No current user' when there is no cached session at all, or with the
        // real underlying error (network failure, or a Cognito exception with a .code) when a
        // cached-but-expired session failed to refresh. That distinction is what the catch below
        // relies on. This app only uses plain Cognito User Pool sign-in (no federated/Hosted UI),
        // which is the only thing currentAuthenticatedUser() adds on top, so this is a safe swap.
        const amplifyUser = await Auth.currentUserPoolUser();
        const userData = amplifyUser.getSignInUserSession().getAccessToken().decodePayload();
        const cognitoGroups: string[] = userData['cognito:groups'] || [];
        const isAdmin = cognitoGroups.includes('adm');
        const isPilot = cognitoGroups.includes('usr');
        const isTechnician = cognitoGroups.includes('tch');
        const isNotPilot = isAdmin && !(isPilot || isTechnician);
        const isOnPcLike = window.screen.width > 1040;
        const userId: string = amplifyUser.getUsername();

        dispatch({ type: COGNITO_USER_SIGN_IN, data: { groups: cognitoGroups, id: userId } });
        if (isImpersonating()) {
            // the impersonated identity is served by the state hooks; the real user's TLB data
            // and the environment auto-switch would only fight with it
            return;
        }
        if (getPreferredEnvironment() !== AppEnvironment.TLB && (isNotPilot || isOnPcLike)) {
            setPreferredEnvironment(AppEnvironment.OPS);
            loadTlbUserForUserId(userId).then((user) => {
                if (user) {
                    dispatch({ type: TLB_USER_SIGN_IN, data: { user, isAdmin, isPilot, isTechnician } });
                } else {
                    dispatch({ type: TLB_USER_SIGN_OUT });
                }
            });
            if (!globalThis.location.pathname.startsWith(ADMIN_LINK_PREFIX)) {
                // hotfix - figure out how to use correct react-router redirect.
                // It's not redirect, that needs to be thrown in a specific action/loader function.
                // So either change this to a action/loader, or change this to a useReauthenticate hook
                // and then use useNavigate.
                globalThis.location.href = `${globalThis.location.origin}${ADMIN_LINK_PREFIX}`;
            }

            return;
        }
        await tryCreateTlbUser(dispatch, amplifyUser);
    } catch (error) {
        console.error('Error when authenticating: ', error);
        if (isGenuineAuthInvalidationError(error)) {
            // There is no usable session: either none is cached at all, or the cached one is
            // definitively invalid rather than merely unreachable. The stale "signed in" app state
            // (persisted to localStorage by the reducers, and deliberately left untouched by the
            // *_AUTH_ERROR dispatches below) has to be cleared for real. A transient network
            // failure while refreshing a still-cached session does not reach this branch.
            Auth.signOut();
        }
        dispatch({ type: TLB_USER_AUTH_ERROR });
        dispatch({ type: COGNITO_USER_AUTH_ERROR });
    }
};

const tryCreateTlbUser = async (dispatch: Dispatch<CognitoUserActionTypes | TlbUserActionTypes>, amplifyUser: any) => {
    const userId = amplifyUser.getUsername();
    const userData = amplifyUser.getSignInUserSession().getAccessToken().decodePayload();
    const cognitoGroups = userData['cognito:groups'] || [];
    const isAdmin = cognitoGroups.includes('adm');
    const isPilot = cognitoGroups.includes('usr');
    const isTechnician = cognitoGroups.includes('tch');
    // try it quickly from cache
    let user = await getUser(userId);
    if (user) {
        dispatch({ type: TLB_USER_SIGN_IN, data: { user, isAdmin, isPilot, isTechnician } });

        return;
    }
    // otherwise, wait for full sync
    await getDbSyncer().initComplete();
    user = await getUser(userId);
    if (user) {
        dispatch({ type: TLB_USER_SIGN_IN, data: { user, isAdmin, isPilot, isTechnician } });

        return;
    }
    throw new Error(`User ${userId} not found`);
};

// Resolves the TLB (crew) identity behind a Cognito user id, online. Also used by the admin
// impersonation screen to build the identity it hands to the app.
export const loadTlbUserForUserId = async (userId: string): Promise<User | null> => {
    const response = await executeQuery2(getListContacsByUserIdQuery, { userId });
    if (response.data.listContactsByUserId.items.length) {
        const userData = response.data.listContactsByUserId.items[0];
        if (getContactIsCrewable(userData)) {
            return getUserFromContact(userData, userId);
        }
    }

    return null;
};

type GetListContacsByUserIdQueryReturnType = {
    listContactsByUserId: {
        items: Array<CreateContactInput & DbCreated>;
    };
};

const getListContacsByUserIdQuery: QueryFactory2<GetListContacsByUserIdQueryReturnType, { userId: string }> = (
    variables
) => {
    const query = `
        query ListContactsByUserId(
            $userId: ID!
        ) {
            listContactsByUserId(
                userId: $userId
                limit: 1
            )
            {
                items {
                    ${contactItemFields}
                }
            }
        }
    `;

    return { query, variables };
};
